import React from 'react';

/**
 * Tabela de verdade no HTML (`<table>`, `<th scope>`), para leitor de tela,
 * buscador e IA lerem linhas e colunas como dado — `View` vira `div` solta.
 * A versão nativa (`semantic-table.tsx`) desenha o mesmo com `View`.
 *
 * Os estilos usam as mesmas classes do Tailwind das páginas de SEO; o
 * NativeWind não processa elemento DOM, então aqui é `className` puro.
 */

export interface SemanticTableColumn {
  /** Texto do cabeçalho. */
  label: string;
  /** Destaca a coluna (ex.: a da Controlapag). */
  highlight?: boolean;
}

export interface SemanticTableProps {
  /** Legenda da tabela: diz o que ela compara (vira `<caption>`). */
  caption: string;
  /** Mostrar a legenda na tela (senão fica só para leitor de tela e máquinas). */
  showCaption?: boolean;
  columns: SemanticTableColumn[];
  /** Cada linha: a 1ª célula é o cabeçalho da linha (`<th scope="row">`). */
  rows: string[][];
}

export function SemanticTable({ caption, showCaption = false, columns, rows }: SemanticTableProps) {
  return (
    // `minWidth: 0` e `maxWidth: 100%`: dentro de um `View` (flex) a tabela
    // não pode alargar a página; o que não couber rola só aqui dentro.
    <div
      className="w-full overflow-x-auto rounded-2xl border border-neutral-900/5 bg-card"
      style={{ minWidth: 0, maxWidth: '100%' }}
    >
      <table className="w-full border-collapse text-left text-sm" style={{ wordBreak: 'normal', overflowWrap: 'anywhere' }}>
        <caption
          className={
            showCaption
              ? 'px-4 pt-4 text-left font-semibold text-sm text-neutral-700'
              : 'sr-only'
          }
        >
          {caption}
        </caption>
        <thead className="bg-neutral-50">
          <tr>
            {columns.map((column) => (
              <th
                key={column.label}
                scope="col"
                className={`border-b border-neutral-200 p-4 font-semibold ${
                  column.highlight ? 'text-primary' : 'text-neutral-600'
                }`}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={`${row[0]}-${rowIndex}`} className={rowIndex > 0 ? 'border-t border-neutral-100' : ''}>
              {row.map((cell, cellIndex) =>
                cellIndex === 0 ? (
                  <th key={cellIndex} scope="row" className="p-4 align-top font-semibold text-neutral-900">
                    {cell}
                  </th>
                ) : (
                  <td
                    key={cellIndex}
                    className={`p-4 align-top ${
                      columns[cellIndex]?.highlight ? 'bg-primary-50/40 text-neutral-800' : 'text-neutral-600'
                    }`}
                  >
                    {cell}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
